import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, CheckCircle, Zap, Shield, TrendingUp, Award, Handshake } from 'lucide-react';
import SEO from '../components/SEO';
import { PageHero } from '../components/PageComponents';
import { opdrachtgeverVoordelenData } from '../data/extraMockData';
import { statsData } from '../data/mockData';

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay }} className={className}>
      {children}
    </motion.div>
  );
};

const icons = [Zap, Shield, TrendingUp, Award, Handshake, CheckCircle];

const VoorOpdrachtgeversPage = () => {
  return (
    <main className="pt-28 min-h-screen">
      <SEO title="Voor Opdrachtgevers | Finance & HR professionals - Staffing Force" description="Op zoek naar ervaren Finance of HR professionals? Staffing Force levert binnen 48-72 uur interim, W&S of detavast plaatsingen. 18+ jaar marktkennis, B Corp gecertificeerd." />

      <PageHero
        label="Voor organisaties"
        title="Voor Opdrachtgevers"
        description="Op zoek naar de juiste professional? Wij leveren ervaren Finance en HR professionals die direct resultaat leveren."
        image="https://images.unsplash.com/photo-1552664730-d307ca884978?w=1920&q=85&auto=format&fit=crop"
      />

      {/* Why us */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-16">
              <span className="text-[#00897B] text-sm font-semibold tracking-widest uppercase">Waarom Staffing Force</span>
              <h2 className="text-4xl font-bold text-gray-900 mt-3">Uw betrouwbare partner</h2>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {opdrachtgeverVoordelenData.map((v, i) => {
              const Icon = icons[i] || CheckCircle;
              return (
                <FadeIn key={i} delay={i * 0.08}>
                  <div className="bg-white rounded-xl p-6 border border-gray-100 hover:shadow-md transition-shadow h-full card-hover">
                    <Icon size={24} className="text-[#00897B] mb-4" />
                    <h3 className="font-bold text-gray-900 mb-2">{v.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{v.description}</p>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-16">
              <span className="text-[#00897B] text-sm font-semibold tracking-widest uppercase">Het proces</span>
              <h2 className="text-4xl font-bold text-gray-900 mt-3">Hoe wij werken</h2>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-8">
            {[{ step: '01', title: 'Intake', desc: 'Wij bespreken uw wensen, eisen en de cultuur van uw organisatie.' },
              { step: '02', title: 'Selectie', desc: 'Binnen 48 uur presenteren wij geschikte kandidaten uit ons netwerk.' },
              { step: '03', title: 'Plaatsing', desc: 'Na akkoord start de professional met volledige begeleiding van ons.' }].map((s, i) => (
              <FadeIn key={i} delay={i * 0.15}>
                <div className="bg-white rounded-xl p-8 border border-gray-100 text-center">
                  <span className="text-5xl font-black text-[#00897B]/15">{s.step}</span>
                  <h3 className="text-xl font-bold text-gray-900 -mt-2 mb-3">{s.title}</h3>
                  <p className="text-gray-600 text-sm">{s.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#00897B]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-8">
          {statsData.map((s, i) => (
            <FadeIn key={i} delay={i * 0.1}>
              <div className="text-center">
                <p className="text-4xl font-bold text-white mb-1">{s.number}</p>
                <p className="text-white/80 text-sm">{s.label}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-16">
              <span className="text-[#00897B] text-sm font-semibold tracking-widest uppercase">Flexibele oplossingen</span>
              <h2 className="text-4xl font-bold text-gray-900 mt-3">Onze diensten</h2>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-6">
            {[{ title: 'Interim Management', desc: 'Ervaren professionals voor tijdelijke opdrachten op elk niveau.', link: '/diensten' },
              { title: 'Werving & Selectie', desc: 'De juiste kandidaat voor een vaste positie in uw organisatie.', link: '/diensten' },
              { title: 'Detavast', desc: 'Flexibel starten met de mogelijkheid tot vast dienstverband.', link: '/diensten' }].map((d, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <Link to={d.link} className="block bg-gray-50 rounded-xl p-8 border border-gray-100 hover:shadow-md hover:border-[#00897B]/30 transition-all group h-full">
                  <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#00897B] transition-colors mb-3">{d.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{d.desc}</p>
                  <span className="text-[#00897B] text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">Meer informatie <ArrowRight size={14} /></span>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0d1b2a]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <FadeIn>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Professional nodig?</h2>
            <p className="text-white/70 mb-8">Neem vandaag nog contact op en wij helpen u aan het juiste talent.</p>
            <Link to="/contact" className="bg-[#00897B] hover:bg-[#00796B] text-white px-8 py-3 rounded font-semibold text-sm transition-all hover:-translate-y-0.5 inline-flex items-center gap-2">Neem contact op <ArrowRight size={16} /></Link>
          </FadeIn>
        </div>
      </section>
    </main>
  );
};

export default VoorOpdrachtgeversPage;
